import h from 'hyperscript';

import { slotSummary } from 'ui/section/section';
import sectionStyles from 'ui/section/section.module.css';

import { uniqueId } from 'cadenza/utils/unique-id';

import disclosureStyles from './disclosure.module.css';
import './disclosure.css';

const COMPONENT_NAME = 'd-disclosure';

export class Disclosure extends HTMLElement {
  #summary: HTMLButtonElement;

  static get observedAttributes() {
    return ['expanded', 'non-toggleable', 'name'];
  }

  constructor() {
    super();
    const contentId = uniqueId();
    const shadowRoot = this.attachShadow({ mode: 'open' });
    shadowRoot.append(
      h('.header', [
        (this.#summary = h(
          'button.summary.focus-outline-keynav',
          {
            type: 'button',
            attrs: { 'aria-controls': contentId },
            onclick: () => this.#toggleExpanded(),
          },
          [h('slot', { name: 'summary' })],
        )),
        h('slot.summary-aside', { name: 'summary-aside' }),
        h('button.expanded-toggle', {
          type: 'button',
          attrs: { 'aria-hidden': 'true' },
          tabIndex: -1, // Remove from tab order to save a redundant tab stop.
          onclick: () => this.#toggleExpanded(),
        }),
      ]),
      h('slot.content', { id: contentId, part: 'content' }),
    );
    shadowRoot.adoptedStyleSheets = [sectionStyles, disclosureStyles];
  }

  #toggleExpanded() {
    this.expanded = !this.expanded;
    this.dispatchEvent(new CustomEvent('toggle'));
  }

  #closeHomonymDisclosures() {
    if (this.expanded && this.name) {
      // Note that this only affects disclosures that are currently in the light dom
      document.querySelectorAll<Disclosure>(`.${COMPONENT_NAME}[name="${this.name}"]`).forEach((disclosure) => {
        if (disclosure !== this) {
          disclosure.expanded = false;
        }
      });
    }
  }

  connectedCallback() {
    this.classList.add(COMPONENT_NAME);
    slotSummary(this);
  }

  attributeChangedCallback() {
    this.#summary.setAttribute('aria-expanded', String(this.expanded));
    this.#summary.disabled = this.nonToggleable;
    this.#closeHomonymDisclosures();
  }

  get name() {
    return this.getAttribute('name') ?? '';
  }

  set name(value: string) {
    this.setAttribute('name', value);
  }

  get nonToggleable() {
    return this.hasAttribute('non-toggleable');
  }

  set nonToggleable(value: boolean) {
    this.toggleAttribute('non-toggleable', value);
  }

  get expanded() {
    return this.hasAttribute('expanded');
  }

  set expanded(value: boolean) {
    this.toggleAttribute('expanded', value);
  }
}

customElements.define(COMPONENT_NAME, Disclosure);
declare global {
  interface HTMLElementTagNameMap {
    [COMPONENT_NAME]: Disclosure;
  }
}
